import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  ArrowRight, Package, Truck, User, MapPin, CreditCard,
  Clock, CheckCircle, XCircle, AlertCircle, ChevronDown, X,
  Printer, FileText,
} from 'lucide-react'
import { useState } from 'react'
import { ordersService } from '@/services/ordersService'
import LoadingSpinner from '@/components/shared/LoadingSpinner'
import StatusBadge from '@/components/shared/StatusBadge'
import { formatCurrency, formatDateTime, getLocalizedField, ORDER_STATUS_LABELS } from '@/utils/formatters'
import { ROUTES } from '@/constants/routes'
import { usePlatformSettings } from '@/contexts/PlatformSettingsContext'

const ORDER_STATUSES = ['pending', 'confirmed', 'processing', 'shipped', 'delivered', 'cancelled']

// ── Print helpers ──────────────────────────────────────────────────────────────
function printInvoice(order, settings, t, lang) {
  const isAr = lang === 'ar'
  const paymentLabels = {
    card: t('orders.paymentMethodsPrint.card'), cash_on_delivery: t('orders.paymentMethodsPrint.cashOnDelivery'),
    cod: t('orders.paymentMethodsPrint.cashOnDelivery'), online: t('orders.paymentMethodsPrint.online'),
    bank_transfer: t('orders.paymentMethodsPrint.bankTransfer'), wallet: t('orders.paymentMethodsPrint.wallet'),
  }

  const rows = (order.items || []).map((item) => `
    <tr>
      <td>${(isAr ? item.productSnapshot?.name?.ar : item.productSnapshot?.name?.en) || item.productSnapshot?.name?.ar || item.productSnapshot?.name?.en || (isAr ? item.product?.name?.ar : item.product?.name?.en) || item.product?.name?.ar || item.product?.name?.en || '—'}</td>
      <td>${(isAr ? item.productSnapshot?.vendorStoreName?.ar : item.productSnapshot?.vendorStoreName?.en) || item.productSnapshot?.vendorStoreName?.ar || item.productSnapshot?.vendorStoreName?.en || '—'}</td>
      <td style="text-align:center">${item.quantity}</td>
      <td style="text-align:left" dir="ltr">${formatCurrency(item.unitPrice)}</td>
      <td style="text-align:left;font-weight:700;color:#FF6D00" dir="ltr">${formatCurrency(item.subtotal)}</td>
    </tr>
  `).join('')

  const html = `
    <!DOCTYPE html><html dir="${isAr ? 'rtl' : 'ltr'}" lang="${lang}">
    <head>
      <meta charset="UTF-8">
      <title>${t('orders.invoice.title')} #${order.orderNumber}</title>
      <style>
        * { margin:0; padding:0; box-sizing:border-box; }
        body { font-family: 'Cairo', 'Tajawal', Arial, sans-serif; color:#1A1C1C; background:#fff; padding:32px; }
        .header { display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:28px; padding-bottom:20px; border-bottom:2px solid #000666; }
        .logo { display:flex; align-items:center; gap:10px; }
        .logo-mark { width:38px; height:38px; flex-shrink:0; }
        .logo-text { font-size:24px; font-weight:900; color:#000666; }
        .logo-text span { color:#FF6D00; }
        .invoice-title { text-align:left; }
        .invoice-title h1 { font-size:22px; font-weight:900; color:#000666; }
        .invoice-title p { font-size:12px; color:#767683; margin-top:4px; }
        .grid2 { display:grid; grid-template-columns:1fr 1fr; gap:20px; margin-bottom:24px; }
        .section { background:#F9F9F9; border-radius:10px; padding:14px; }
        .section h3 { font-size:11px; font-weight:700; color:#767683; text-transform:uppercase; letter-spacing:0.06em; margin-bottom:10px; }
        .info-row { display:flex; justify-content:space-between; padding:4px 0; font-size:13px; }
        .info-row span:last-child { font-weight:600; }
        table { width:100%; border-collapse:collapse; margin-bottom:20px; }
        thead tr { background:#000666; color:#fff; }
        thead th { padding:10px 12px; font-size:12px; font-weight:700; text-align:right; }
        tbody tr { border-bottom:1px solid #F3F3F3; }
        tbody td { padding:10px 12px; font-size:13px; }
        tbody tr:nth-child(even) { background:#F9F9F9; }
        .totals { margin-right:auto; margin-left:0; width:260px; }
        .totals-row { display:flex; justify-content:space-between; padding:6px 0; font-size:13px; border-bottom:1px solid #F3F3F3; }
        .totals-row.total { font-weight:900; font-size:16px; color:#FF6D00; border-top:2px solid #000666; border-bottom:none; padding-top:10px; }
        .footer { margin-top:28px; text-align:center; font-size:11px; color:#767683; border-top:1px solid #F3F3F3; padding-top:16px; }
        @media print { body { padding:16px; } }
      </style>
    </head>
    <body>
      <div class="header">
        <div class="logo">
          ${settings?.logo ? `<img src="${settings.logo}" alt="Logo" class="logo-mark" style="object-fit: contain;" />` : `<svg class="logo-mark" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="32" height="32" rx="7" fill="#000666"/>
            <rect x="9.4" y="8.5" width="3.6" height="15" rx="1.2" fill="#fff"/>
            <rect x="19" y="8.5" width="3.6" height="15" rx="1.2" fill="#fff"/>
            <rect x="9.4" y="14.2" width="13.2" height="3.6" rx="1.2" fill="#fff"/>
            <circle cx="24.2" cy="23.4" r="2.7" fill="#FF6D00"/>
          </svg>`}
          <span class="logo-text">${settings?.platformName?.ar || 'حاجاتنا'} <span>•</span> ${settings?.platformName?.en || 'Hagatnaa'}</span>
        </div>
        <div class="invoice-title">
          <h1>${t('orders.invoice.title')}</h1>
          <p>${t('orders.invoice.invoiceNo')} #${order.orderNumber}</p>
          <p>${t('orders.invoice.date')} ${formatDateTime(order.createdAt)}</p>
        </div>
      </div>

      <div class="grid2">
        <div class="section">
          <h3>${t('orders.invoice.customerInfo')}</h3>
          <div class="info-row"><span>${t('orders.invoice.name')}</span><span>${order.user?.name || '—'}</span></div>
          <div class="info-row"><span>${t('orders.invoice.email')}</span><span>${order.user?.email || '—'}</span></div>
          <div class="info-row"><span>${t('orders.invoice.phone')}</span><span>${order.user?.phone || '—'}</span></div>
        </div>
        <div class="section">
          <h3>${t('orders.invoice.deliveryAddress')}</h3>
          <div class="info-row"><span>${t('orders.invoice.recipient')}</span><span>${order.shippingAddress?.recipientName || '—'}</span></div>
          <div class="info-row"><span>${t('orders.invoice.phone')}</span><span>${order.shippingAddress?.phone || '—'}</span></div>
          <div class="info-row"><span>${t('orders.invoice.address')}</span><span>${order.shippingAddress?.street || '—'}</span></div>
          <div class="info-row"><span>${t('orders.invoice.city')}</span><span>${order.shippingAddress?.city || '—'}${order.shippingAddress?.country ? ' — ' + order.shippingAddress.country : ''}</span></div>
        </div>
      </div>

      <table>
        <thead>
          <tr>
            <th>${t('orders.invoice.product')}</th><th>${t('orders.invoice.vendor')}</th><th style="text-align:center">${t('orders.invoice.quantity')}</th>
            <th style="text-align:left">${t('orders.invoice.price')}</th><th style="text-align:left">${t('orders.invoice.total')}</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>

      <div style="display:flex;justify-content:flex-end">
        <div class="totals">
          <div class="totals-row"><span>${t('orders.invoice.subtotal')}</span><span>${formatCurrency(order.subtotal)}</span></div>
          ${order.discountAmount > 0 ? `<div class="totals-row"><span>${t('orders.invoice.discount')}</span><span>- ${formatCurrency(order.discountAmount)}</span></div>` : ''}
          <div class="totals-row"><span>${t('orders.invoice.shipping')}</span><span>${formatCurrency(order.shippingFee)}</span></div>
          <div class="totals-row total"><span>${t('orders.invoice.total')}</span><span>${formatCurrency(order.total)}</span></div>
        </div>
      </div>

      <div class="footer">
        ${t('orders.invoice.footerThanks')}<br/>
        ${t('orders.invoice.paymentMethod')} ${paymentLabels[order.paymentMethod] || order.paymentMethod || '—'}
        ${order.couponCode ? ` | ${t('orders.invoice.couponCode')} ${order.couponCode}` : ''}
      </div>
    </body></html>
  `
  const win = window.open('', '_blank', 'width=900,height=700')
  win.document.write(html)
  win.document.close()
  win.focus()
  setTimeout(() => win.print(), 400)
}

function printShippingLabel(order, settings, t, lang) {
  const isAr = lang === 'ar'
  const qr = `https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent(order.orderNumber)}`
  const shipment = order.shipments?.[0]

  const itemRows = (order.items || []).map((item) => `
    <tr>
      <td style="padding:6px 0;font-size:13px;font-weight:600;color:#1A1C1C;border-bottom:1px solid #F3F3F3;">
        ${(isAr ? item.productSnapshot?.name?.ar : item.productSnapshot?.name?.en) || item.productSnapshot?.name?.ar || item.productSnapshot?.name?.en || (isAr ? item.product?.name?.ar : item.product?.name?.en) || item.product?.name?.ar || item.product?.name?.en || '—'}
      </td>
      <td style="padding:6px 0;font-size:13px;font-weight:700;color:#000666;text-align:center;border-bottom:1px solid #F3F3F3;">
        ×${item.quantity}
      </td>
    </tr>
  `).join('')

  const html = `
    <!DOCTYPE html><html dir="${isAr ? 'rtl' : 'ltr'}" lang="${lang}">
    <head>
      <meta charset="UTF-8">
      <title>${t('orders.shippingLabel.title')} #${order.orderNumber}</title>
      <style>
        * { margin:0; padding:0; box-sizing:border-box; }
        body { font-family: 'Cairo', 'Tajawal', Arial, sans-serif; background:#fff; display:flex; align-items:center; justify-content:center; min-height:100vh; }
        .label { width:420px; border:2.5px solid #000; border-radius:12px; padding:20px; }
        .label-header { display:flex; justify-content:space-between; align-items:center; padding-bottom:14px; border-bottom:2px dashed #000; margin-bottom:14px; }
        .brand { display:flex; align-items:center; gap:8px; font-size:20px; font-weight:900; color:#000666; }
        .brand svg { width:30px; height:30px; }
        .order-num { font-size:13px; font-weight:700; color:#767683; }
        .addr-block { margin-bottom:14px; }
        .addr-block h4 { font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:0.06em; color:#767683; margin-bottom:6px; }
        .addr-block p { font-size:15px; font-weight:700; color:#1A1C1C; line-height:1.7; }
        .items-block { margin-bottom:14px; }
        .items-block h4 { font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:0.06em; color:#767683; margin-bottom:8px; }
        .items-block table { width:100%; border-collapse:collapse; }
        .separator { border-top:1px dashed #999; margin:14px 0; }
        .footer { display:flex; justify-content:space-between; align-items:center; }
        .tracking { font-size:11px; color:#767683; }
        .tracking strong { display:block; font-size:14px; color:#000666; margin-top:2px; direction:ltr; }
        @media print { body { min-height:auto; } .label { border:2px solid #000; } }
      </style>
    </head>
    <body>
      <div class="label">
        <div class="label-header">
          <div>
            <div class="brand">
              ${settings?.logo ? `<img src="${settings.logo}" alt="Logo" style="width:32px; height:32px; border-radius:7px; object-fit: contain;" />` : `<svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="32" height="32" rx="7" fill="#000666"/>
                <rect x="9.4" y="8.5" width="3.6" height="15" rx="1.2" fill="#fff"/>
                <rect x="19" y="8.5" width="3.6" height="15" rx="1.2" fill="#fff"/>
                <rect x="9.4" y="14.2" width="13.2" height="3.6" rx="1.2" fill="#fff"/>
                <circle cx="24.2" cy="23.4" r="2.7" fill="#FF6D00"/>
              </svg>`}
              ${settings?.platformName?.[lang] || settings?.platformName?.ar || (isAr ? 'حاجاتنا' : 'Hagatnaa')}
            </div>
            <div class="order-num">#${order.orderNumber}</div>
          </div>
          <img src="${qr}" width="80" height="80" alt="QR" />
        </div>

        <div class="addr-block">
          <h4>${t('orders.shippingLabel.to')}</h4>
          <p>
            ${order.shippingAddress?.recipientName || '—'}<br/>
            ${order.shippingAddress?.phone || ''}<br/>
            ${order.shippingAddress?.street || ''}<br/>
            ${order.shippingAddress?.city || ''}${order.shippingAddress?.country ? ' — ' + order.shippingAddress.country : ''}
          </p>
        </div>

        <div class="separator"></div>

        <div class="items-block">
          <h4>${t('orders.shippingLabel.items')}</h4>
          <table>${itemRows}</table>
        </div>

        <div class="separator"></div>

        <div class="footer">
          <div class="tracking">
            ${shipment ? `${t('orders.shippingLabel.carrier')} <strong>${shipment.carrier || '—'}</strong>` : ''}
            ${shipment?.trackingNumber ? `${t('orders.shippingLabel.trackingNumber')} <strong>${shipment.trackingNumber}</strong>` : ''}
          </div>
          <div style="text-align:left;font-size:11px;color:#767683">
            ${order.total ? `<strong style="font-size:14px;color:#FF6D00">${formatCurrency(order.total)}</strong><br/>` : ''}
            ${['cash_on_delivery','cod'].includes(order.paymentMethod) ? `<span style="color:#BA1A1A;font-weight:700">${t('orders.shippingLabel.cod')}</span>` : `<span style="color:#1B6D24;font-weight:700">${t('orders.shippingLabel.paid')}</span>`}
          </div>
        </div>
      </div>
    </body></html>
  `
  const win = window.open('', '_blank', 'width=500,height=500')
  win.document.write(html)
  win.document.close()
  win.focus()
  setTimeout(() => win.print(), 400)
}

function Section({ icon: Icon, title, children, color = '#000666' }) {
  return (
    <div className="hg-card p-5">
      <div className="flex items-center gap-2.5 mb-4" style={{ borderBottom: '1px solid #F3F3F3', paddingBottom: 14 }}>
        <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: `${color}12`, color }}>
          <Icon size={16} />
        </div>
        <h3 className="font-black text-navy" style={{ fontFamily: 'Cairo, sans-serif', fontSize: 15 }}>{title}</h3>
      </div>
      {children}
    </div>
  )
}

function InfoRow({ label, value, bold }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: '1px solid #F9F9F9' }}>
      <span style={{ color: '#767683', fontSize: 12.5, fontFamily: 'Tajawal, sans-serif' }}>{label}</span>
      <span style={{ color: bold ? '#FF6D00' : '#1A1C1C', fontWeight: bold ? 800 : 600, fontSize: 13, fontFamily: 'Cairo, sans-serif' }}>{value}</span>
    </div>
  )
}

function StatusBubble({ status, date, lang }) {
  const meta = ORDER_STATUS_LABELS[status]
  const colors = {
    pending:    '#FF6D00',
    confirmed:  '#1B6D24',
    processing: '#000666',
    shipped:    '#0077B6',
    delivered:  '#1B6D24',
    cancelled:  '#BA1A1A',
  }
  const c = colors[status] || '#767683'
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0 }}>
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: c, marginTop: 3, flexShrink: 0 }} />
        <div style={{ width: 1.5, background: '#E8E8E8', flex: 1, minHeight: 24 }} />
      </div>
      <div style={{ paddingBottom: 12 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: c, fontFamily: 'Cairo, sans-serif' }}>
          {meta?.[lang === 'ar' ? 'ar' : 'en'] || status}
        </div>
        {date && (
          <div style={{ fontSize: 11, color: '#767683', fontFamily: 'Tajawal, sans-serif', marginTop: 2 }}>
            {formatDateTime(date)}
          </div>
        )}
      </div>
    </div>
  )
}

export default function OrderDetailPage() {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const { orderNumber } = useParams()
  const navigate = useNavigate()
  const qc = useQueryClient()

  const [overrideOpen, setOverrideOpen] = useState(false)
  const { settings } = usePlatformSettings()
  const [newStatus, setNewStatus] = useState('')
  const [note, setNote] = useState('')
  const [markPaidOpen, setMarkPaidOpen] = useState(false)

  const { data: order, isLoading, isError } = useQuery({
    queryKey: ['order-detail-admin', orderNumber],
    queryFn: () => ordersService.adminGetDetail(orderNumber),
    retry: 1,
    enabled: !!orderNumber,
  })

  const markPaidMut = useMutation({
    mutationFn: () => ordersService.markPaid(orderNumber),
    onSuccess: (data) => {
      toast.success(t('orders.markedPaid'))
      // Update payment status in place from the response, keeping the rich detail
      // (items, shipments, history) the mark-paid response doesn't return.
      qc.setQueryData(['order-detail-admin', orderNumber], (prev) =>
        prev ? { ...prev, paymentStatus: data?.paymentStatus ?? 'completed' } : prev
      )
      qc.invalidateQueries({ queryKey: ['orders-admin'] })
      setMarkPaidOpen(false)
    },
    onError: (e) => toast.error(e?.response?.data?.message || t('orders.markPaidError')),
  })

  const overrideMut = useMutation({
    mutationFn: () => ordersService.updateStatus(orderNumber, newStatus, note),
    onSuccess: () => {
      toast.success(t('orders.statusUpdated'))
      qc.invalidateQueries({ queryKey: ['order-detail-admin', orderNumber] })
      qc.invalidateQueries({ queryKey: ['orders-admin'] })
      setOverrideOpen(false)
      setNewStatus('')
      setNote('')
    },
    onError: (e) => toast.error(e?.response?.data?.message || t('orders.statusUpdateFailed')),
  })

  if (isLoading) return (
    <div className="flex items-center justify-center py-32"><LoadingSpinner /></div>
  )

  if (isError || !order) return (
    <div className="flex flex-col items-center justify-center py-32 gap-4">
      <AlertCircle size={40} style={{ color: '#BA1A1A' }} />
      <p style={{ color: '#BA1A1A', fontWeight: 700, fontFamily: 'Cairo, sans-serif' }}>{t('orders.loadError')}</p>
      <button onClick={() => navigate(ROUTES.ORDERS)} className="hg-btn-navy px-5 py-2 text-sm">{t('orders.backToOrders')}</button>
    </div>
  )

  const paymentLabels = { card: t('orders.paymentMethodsPrint.card'), cash_on_delivery: t('orders.paymentMethodsPrint.cashOnDelivery'), cod: t('orders.paymentMethodsPrint.cashOnDelivery'), online: t('orders.paymentMethodsPrint.online'), bank_transfer: t('orders.paymentMethodsPrint.bankTransfer'), wallet: t('orders.paymentMethodsPrint.wallet') }

  // Manual "mark as paid" is only relevant while the order is unpaid and not cancelled.
  const canMarkPaid = order.paymentStatus !== 'completed' && order.status !== 'cancelled'

  return (
    <div className="space-y-5">

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(ROUTES.ORDERS)}
            className="w-9 h-9 rounded-xl flex items-center justify-center transition-all hover:bg-surface-canvas"
            style={{ background: 'rgba(0,6,102,0.06)', color: '#000666' }}>
            <ArrowRight size={18} />
          </button>
          <div>
            <h1 className="font-black text-navy text-xl" style={{ fontFamily: 'Cairo, sans-serif' }}>
              {t('orders.orderHeading', { number: order.orderNumber })}
            </h1>
            <p className="text-xs text-ghost mt-0.5" style={{ fontFamily: 'Tajawal, sans-serif' }}>
              {formatDateTime(order.createdAt)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <StatusBadge status={order.status} type="order" lang={lang} />
          <StatusBadge status={order.paymentStatus} type="payment" lang={lang} />
          {canMarkPaid && (
            <button
              onClick={() => setMarkPaidOpen(true)}
              className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-bold"
              style={{ background: 'rgba(27,109,36,0.08)', color: '#1B6D24', fontFamily: 'Cairo, sans-serif', border: 'none', cursor: 'pointer' }}
            >
              <CheckCircle size={15} />
              {t('orders.markAsPaidBtn')}
            </button>
          )}
          <button
            onClick={() => printShippingLabel(order, settings, t, lang)}
            className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-bold"
            style={{ background: 'rgba(0,119,182,0.08)', color: '#0077B6', fontFamily: 'Cairo, sans-serif', border: 'none', cursor: 'pointer' }}
          >
            <Printer size={15} />
            {t('orders.shippingLabelBtn')}
          </button>
          <button
            onClick={() => printInvoice(order, settings, t, lang)}
            className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-bold"
            style={{ background: 'rgba(27,109,36,0.08)', color: '#1B6D24', fontFamily: 'Cairo, sans-serif', border: 'none', cursor: 'pointer' }}
          >
            <FileText size={15} />
            {t('orders.printInvoiceBtn')}
          </button>
          <button
            onClick={() => setOverrideOpen(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold"
            style={{ background: '#000666', color: '#fff', fontFamily: 'Cairo, sans-serif', border: 'none', cursor: 'pointer' }}
          >
            {t('orders.override')}
          </button>
        </div>
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">

        {/* Left col — 2/3 */}
        <div className="xl:col-span-2 space-y-5">

          {/* Order Items */}
          <Section icon={Package} title={t('orders.itemsSectionTitle')}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #F3F3F3' }}>
                    {[t('orders.productHeader'), t('orders.vendorHeader'), t('orders.quantityHeader'), t('orders.priceHeader'), t('orders.total'), t('orders.status')].map((h) => (
                      <th key={h} style={{ textAlign: 'right', padding: '8px 12px', fontSize: 11, fontWeight: 700, color: '#767683', fontFamily: 'Tajawal, sans-serif' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(order.items || []).map((item, idx) => (
                    <tr key={item.id || idx} style={{ borderBottom: '1px solid #F9F9F9' }}>
                      <td style={{ padding: '10px 12px' }}>
                        {(() => {
                            const productId = item.product?.id || item.productId
                            const name = getLocalizedField(item.productSnapshot?.name, lang) || getLocalizedField(item.product?.name, lang) || '—'
                            const nameStyle = { fontFamily: 'Cairo, sans-serif', fontWeight: 700, fontSize: 13 }
                            // Deep-link to the product's detail page
                            return productId ? (
                              <button
                                type="button"
                                title={t('orders.viewProductDetails')}
                                onClick={() => navigate(ROUTES.PRODUCT_DETAIL.replace(':id', productId))}
                                style={{ ...nameStyle, color: '#000666', background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'right', textDecoration: 'underline', textUnderlineOffset: 3 }}
                              >
                              {name}
                            </button>
                          ) : (
                            <div style={{ ...nameStyle, color: '#1A1C1C' }}>{name}</div>
                          )
                        })()}
                        {item.variantName && (
                          <div style={{ fontSize: 11, color: '#767683', fontFamily: 'Tajawal, sans-serif', marginTop: 2 }}>{item.variantName}</div>
                        )}
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        <span style={{ fontSize: 12, color: '#454652', fontFamily: 'Tajawal, sans-serif' }}>
                          {getLocalizedField(item.productSnapshot?.vendorStoreName, lang) || '—'}
                        </span>
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        <span style={{ fontFamily: 'Cairo, sans-serif', fontWeight: 700, fontSize: 13, color: '#1A1C1C' }}>{item.quantity}</span>
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        <span style={{ fontFamily: 'Cairo, sans-serif', fontWeight: 700, fontSize: 13, color: '#454652' }}>{formatCurrency(item.unitPrice)}</span>
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        <span style={{ fontFamily: 'Cairo, sans-serif', fontWeight: 800, fontSize: 14, color: '#FF6D00' }}>{formatCurrency(item.subtotal)}</span>
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        <StatusBadge status={item.status} type="order" lang={lang} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          {/* Shipments */}
          {order.shipments?.length > 0 && (
            <Section icon={Truck} title={t('orders.shippingInfoTitle')} color="#0077B6">
              {order.shipments.map((s, i) => (
                <div key={s.id || i} style={{ padding: '12px', borderRadius: 12, background: 'rgba(0,119,182,0.05)', border: '1px solid rgba(0,119,182,0.15)', marginBottom: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                    <div>
                      <div style={{ fontFamily: 'Cairo, sans-serif', fontWeight: 700, fontSize: 13 }}>{s.carrier || t('orders.carrierFallback')}</div>
                      <div style={{ fontFamily: 'Tajawal, sans-serif', fontSize: 12, color: '#767683', marginTop: 2 }}>
                        {t('orders.trackingNumberLabel')} <span dir="ltr" style={{ fontWeight: 700, color: '#000666' }}>{s.trackingNumber || '—'}</span>
                      </div>
                    </div>
                    <div style={{ textAlign: 'left' }}>
                      <div style={{ fontSize: 11, color: '#767683', fontFamily: 'Tajawal, sans-serif' }}>{t('orders.estimatedDeliveryLabel')}</div>
                      <div style={{ fontFamily: 'Cairo, sans-serif', fontWeight: 700, fontSize: 12 }}>{s.estimatedDelivery || '—'}</div>
                    </div>
                  </div>
                </div>
              ))}
            </Section>
          )}

          {/* Status History */}
          {order.statusHistory?.length > 0 && (
            <Section icon={Clock} title={t('orders.statusHistoryTitle')}>
              <div style={{ paddingRight: 4 }}>
                {order.statusHistory.map((h, i) => (
                  <StatusBubble key={i} status={h.status} date={h.createdAt} lang={lang} />
                ))}
              </div>
            </Section>
          )}
        </div>

        {/* Right col — 1/3 */}
        <div className="space-y-5">

          {/* Customer */}
          <Section icon={User} title={t('orders.customerInfoTitle')} color="#FF6D00">
            <InfoRow label={t('users.name')} value={order.user?.name || '—'} />
            <InfoRow label={t('users.email')} value={order.user?.email || '—'} />
            <InfoRow label={t('users.phone')} value={order.user?.phone || '—'} />
          </Section>

          {/* Shipping Address */}
          <Section icon={MapPin} title={t('orders.deliveryAddressTitle')} color="#1B6D24">
            <div style={{ fontFamily: 'Tajawal, sans-serif', fontSize: 13, color: '#454652', lineHeight: 1.8 }}>
              <div style={{ fontWeight: 700, color: '#1A1C1C' }}>{order.shippingAddress?.recipientName || '—'}</div>
              <div>{order.shippingAddress?.phone}</div>
              <div>{order.shippingAddress?.street}</div>
              <div>{order.shippingAddress?.city}{order.shippingAddress?.country ? ` — ${order.shippingAddress.country}` : ''}</div>
            </div>
          </Section>

          {/* Payment */}
          <Section icon={CreditCard} title={t('orders.payment')}>
            <InfoRow label={t('orders.paymentMethodLabel')} value={paymentLabels[order.paymentMethod] || order.paymentMethod || '—'} />
            <InfoRow label={t('orders.paymentStatusLabel')} value={
              <StatusBadge status={order.paymentStatus} type="payment" lang={lang} />
            } />
            <div style={{ marginTop: 12, padding: '10px 0', borderTop: '1px dashed #E8E8E8' }}>
              <InfoRow label={t('orders.subtotalLabel')} value={formatCurrency(order.subtotal)} />
              {order.discountAmount > 0 && (
                <InfoRow label={t('orders.discountLabel')} value={`- ${formatCurrency(order.discountAmount)}`} />
              )}
              <InfoRow label={t('orders.shippingHeader')} value={formatCurrency(order.shippingFee)} />
              <InfoRow label={t('orders.total')} value={formatCurrency(order.total)} bold />
            </div>
            {order.couponCode && (
              <div style={{ marginTop: 8, padding: '6px 10px', borderRadius: 8, background: 'rgba(27,109,36,0.08)', color: '#1B6D24', fontSize: 12, fontFamily: 'Cairo, sans-serif', fontWeight: 700 }}>
                {t('orders.couponLabel')} {order.couponCode}
              </div>
            )}
          </Section>

          {/* Notes */}
          {order.notes && (
            <div className="hg-card p-4">
              <div style={{ fontSize: 12, fontWeight: 700, color: '#767683', fontFamily: 'Tajawal, sans-serif', marginBottom: 6 }}>{t('orders.notesTitle')}</div>
              <p style={{ fontSize: 13, color: '#454652', fontFamily: 'Tajawal, sans-serif', lineHeight: 1.7 }}>{order.notes}</p>
            </div>
          )}
        </div>
      </div>

      {/* Override Status Dialog */}
      {overrideOpen && (
        <div onClick={() => setOverrideOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 24, padding: 28, width: '100%', maxWidth: 420, boxShadow: '0 32px 64px rgba(0,0,0,0.2)', position: 'relative' }}>
            <button onClick={() => setOverrideOpen(false)} style={{ position: 'absolute', top: 16, left: 16, width: 32, height: 32, borderRadius: 10, background: '#F3F3F3', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#767683' }}>
              <X size={16} />
            </button>
            <h3 style={{ fontFamily: 'Cairo, sans-serif', fontWeight: 800, fontSize: 17, color: '#000666', marginBottom: 20 }}>{t('orders.overrideDialogTitle')}</h3>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#454652', marginBottom: 6, fontFamily: 'Tajawal, sans-serif' }}>{t('orders.newStatusLabel')}</label>
              <div className="relative">
                <select className="hg-input" style={{ paddingLeft: 28 }} value={newStatus} onChange={(e) => setNewStatus(e.target.value)}>
                  <option value="">{t('orders.selectStatusPlaceholder')}</option>
                  {ORDER_STATUSES.map((s) => (
                    <option key={s} value={s}>{ORDER_STATUS_LABELS[s]?.[lang === 'ar' ? 'ar' : 'en'] || s}</option>
                  ))}
                </select>
                <ChevronDown size={14} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: '#767683', pointerEvents: 'none' }} />
              </div>
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#454652', marginBottom: 6, fontFamily: 'Tajawal, sans-serif' }}>{t('orders.noteLabel')}</label>
              <textarea className="hg-input" rows={2} placeholder={t('orders.reasonPlaceholder')} value={note} onChange={(e) => setNote(e.target.value)} style={{ resize: 'none' }} />
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setOverrideOpen(false)} className="hg-btn-ghost px-5 py-2.5 text-sm">{t('common.cancel')}</button>
              <button
                onClick={() => overrideMut.mutate()}
                disabled={!newStatus || overrideMut.isPending}
                className="hg-btn-navy px-5 py-2.5 text-sm"
                style={{ opacity: !newStatus || overrideMut.isPending ? 0.6 : 1 }}
              >
                {overrideMut.isPending ? t('orders.updating') : t('orders.updateStatusBtn')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mark as Paid confirm dialog */}
      {markPaidOpen && (
        <div onClick={() => !markPaidMut.isPending && setMarkPaidOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 24, padding: 28, width: '100%', maxWidth: 420, boxShadow: '0 32px 64px rgba(0,0,0,0.2)', position: 'relative' }}>
            <button onClick={() => setMarkPaidOpen(false)} disabled={markPaidMut.isPending} style={{ position: 'absolute', top: 16, left: 16, width: 32, height: 32, borderRadius: 10, background: '#F3F3F3', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#767683' }}>
              <X size={16} />
            </button>
            <div className="w-11 h-11 rounded-2xl flex items-center justify-center mb-4" style={{ background: 'rgba(27,109,36,0.1)', color: '#1B6D24' }}>
              <CheckCircle size={22} />
            </div>
            <h3 style={{ fontFamily: 'Cairo, sans-serif', fontWeight: 800, fontSize: 17, color: '#000666', marginBottom: 8 }}>{t('orders.markPaidDialogTitle')}</h3>
            <p style={{ fontFamily: 'Tajawal, sans-serif', fontSize: 13.5, color: '#454652', lineHeight: 1.7, marginBottom: 22 }}>
              {t('orders.markPaidDialogBody')}
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setMarkPaidOpen(false)} disabled={markPaidMut.isPending} className="hg-btn-ghost px-5 py-2.5 text-sm">{t('common.cancel')}</button>
              <button
                onClick={() => markPaidMut.mutate()}
                disabled={markPaidMut.isPending}
                className="px-5 py-2.5 text-sm font-bold"
                style={{ background: '#1B6D24', color: '#fff', borderRadius: 12, border: 'none', cursor: 'pointer', fontFamily: 'Cairo, sans-serif', opacity: markPaidMut.isPending ? 0.6 : 1 }}
              >
                {markPaidMut.isPending ? t('orders.saving') : t('orders.confirmPaymentBtn')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
